import { FILTER_FEATURE } from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { GeneralsInjection } from "features/generals/di/GeneralsInjection";
import { isEmpty } from "lodash";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { ProviderCodeEnum } from "utils/enums/ProviderCodeEnum";
import { DataFormat } from "utils/format/DataFormat";
import { paramsChecking } from "utils/helpersInTs/helpersInTs";
import { GeneralResponseEntity } from "../entities/GeneralResponseEntity";
import { OverviewServiceItem } from "../entities/OverviewServiceResponseEntity";
import { PartnerLookupEntity } from "../entities/PartnerLookUpEntity";
import { StaffOptionEntity } from "../entities/StaffOptionEntity";
import { TransactionDetailReportRequestEntity } from "../entities/TransactionDetailReportRequestEntity";
import { TransactionDetailReportResponseEntity } from "../entities/TransactionDetailReportResponseEntity";
import { TransactionReportRequestEntity } from "../entities/TransactionReportRequestEntity";
import { TransactionReportResponseEntity } from "../entities/TransactionReportResponseEntity";
import { IGeneralRepository } from "../repositories/IGeneralRepository";

interface GeneralFilterWorkflowInput {
  serviceCode: string;
  telco?: string[];
  selectedStores?: string[];
  selectedStaffs?: string[];
  staffOptions?: StaffOptionEntity[];
  partnerRefCode?: string;
  partnerList?: PartnerLookupEntity[];
}

@injectable()
export class GeneralsUseCase {
  private generalsRepo: IGeneralRepository;
  private filterUseCase: FilterUseCase;

  constructor() {
    this.generalsRepo = GeneralsInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
  }

  buildExportTransactionDetailReportParams(
    input: Omit<
      GeneralFilterWorkflowInput,
      "selectedStores" | "selectedStaffs"
    > & {
      reportType: number;
      extras?: { store?: string; staff?: string };
    },
  ): TransactionDetailReportRequestEntity {
    const storesInput = Array.isArray(input?.extras?.store)
      ? Array.from(
          new Set(
            (input?.extras?.store as unknown as string[]).filter(Boolean),
          ),
        )
      : input?.extras?.store
        ? [input?.extras?.store as string]
        : [];
    const staffsInput = Array.isArray(input?.extras?.staff)
      ? Array.from(
          new Set(
            (input?.extras?.staff as unknown as string[]).filter(Boolean),
          ),
        )
      : input?.extras?.staff
        ? [input?.extras?.staff as string]
        : [];

    const workflowParams = this.buildReportWorkflowParams({
      serviceCode: input.serviceCode,
      telco: input.telco,
      selectedStores: storesInput,
      selectedStaffs: staffsInput,
      staffOptions: input.staffOptions,
      partnerRefCode: input.partnerRefCode,
      partnerList: input.partnerList,
    });

    return {
      ...workflowParams,
      type: input.reportType,
    };
  }

  private buildStoreStaffIndexedParams(stores: string[], staffs: string[]) {
    const store = stores?.find((item) => !isEmpty(item));
    let dataStaff = {};

    if (staffs?.length > 0) {
      staffs.forEach((item, index) => {
        if (!isEmpty(item)) {
          dataStaff = {
            ...dataStaff,
            [`staff[${index}]`]: item,
          };
        }
      });
    }

    return { store, dataStaff };
  }

  private resolvePartnerFilter(
    refCode?: string,
    partnerList: PartnerLookupEntity[] = [],
  ): Partial<TransactionReportRequestEntity> {
    if (!refCode) return {};

    const partner = partnerList.find(
      (info) => info?.partnerRefCode === refCode || info?.code === refCode,
    );
    if (!partner) return {};

    const value = paramsChecking(refCode, "select");
    if (partner?.providerCode === ProviderCodeEnum.neci)
      return { partnerNeci: value };
    if (partner?.providerCode === "smm") return { partnerSMM: value };
    if (partner?.type === 0 && partner?.providerCode === "b2b") {
      return { partnerB2B: value };
    }
    if (
      partner?.type === 1 &&
      partner?.providerCode === "mobitopup" &&
      !partner?.isStore
    ) {
      return { singleUserB2C: value };
    }
    if (partner?.type === 1 && partner?.isStore) {
      return { partnerChainB2C: value };
    }
    if (partner?.type === 2 && partner?.providerCode === "mobitopup") {
      return { partnerGuest: value };
    }

    return {};
  }

  private normalizeStoreStaffSelection(
    storesInput: string[] = [],
    staffsInput: string[] = [],
    staffOptions: StaffOptionEntity[] = [],
  ) {
    const stores = Array.from(new Set(storesInput.filter(Boolean)));
    const staffsRaw = Array.from(new Set(staffsInput.filter(Boolean)));

    return {
      stores,
      staffs: staffsRaw,
    };
  }

  private buildReportWorkflowParams({
    serviceCode,
    telco,
    selectedStores = [],
    selectedStaffs = [],
    staffOptions = [],
    partnerRefCode,
    partnerList = [],
  }: GeneralFilterWorkflowInput): TransactionReportRequestEntity {
    const baseParams: TransactionReportRequestEntity =
      serviceCode === "ALL" ? {} : { service: serviceCode };
    const normalized = this.normalizeStoreStaffSelection(
      selectedStores,
      selectedStaffs,
      staffOptions,
    );
    const { store, dataStaff } = this.buildStoreStaffIndexedParams(
      normalized.stores,
      normalized.staffs,
    );

    const partnerParams = this.resolvePartnerFilter(
      partnerRefCode,
      partnerList,
    );

    return DataFormat.removeUndefined({
      ...baseParams,
      store,
      ...dataStaff,
      telco: telco?.filter((ele) => !isEmpty(ele)),
      ...partnerParams,
      isOnlyStore:
        !isEmpty(store) && isEmpty(normalized.staffs) ? "true" : undefined,
    });
  }

  private flattenDisplayStat<T>(
    data?: Array<{ displayStat?: Array<T> } & T>,
  ): Array<T> {
    return (data || []).flatMap((item) => {
      if (Array.isArray(item?.displayStat)) {
        return item.displayStat;
      }

      return item ? [item as T] : [];
    });
  }

  buildTransactionDetailReportParams(
    input: GeneralFilterWorkflowInput & {
      reportType: number;
    },
  ): TransactionDetailReportRequestEntity {
    const workflowParams = this.buildReportWorkflowParams(input);
    return {
      ...workflowParams,
      type: input.reportType,
    };
  }

  buildTransactionReportParams(
    input: GeneralFilterWorkflowInput,
  ): TransactionReportRequestEntity {
    return this.buildReportWorkflowParams(input);
  }

  async getGeneralData(): Promise<
    ListBaseApiResponseModel<GeneralResponseEntity>
  > {
    return await this.generalsRepo.getGeneralData();
  }

  async getTransactionReport(
    params: TransactionReportRequestEntity,
  ): Promise<ListBaseApiResponseModel<TransactionReportResponseEntity>> {
    return await this.generalsRepo.getTransactionReport(params);
  }

  async getTransactionDetailReport(
    params: TransactionDetailReportRequestEntity,
  ): Promise<ListBaseApiResponseModel<TransactionDetailReportResponseEntity>> {
    return await this.generalsRepo.getTransactionDetailReport(params);
  }

  async exportTransactionDetailReport(
    params: TransactionDetailReportRequestEntity,
  ): Promise<BaseApiResponseModel<Blob>> {
    return await this.generalsRepo.exportTransactionDetailReport(params);
  }

  async getOverviewServiceList(): Promise<Array<OverviewServiceItem>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.SERVICE_LIST,
      isNeedFeature: true,
      savedKey: CacheKeyEnum.serviceList,
      params: {},
    });
  }

  async getCarrierList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.CARRIER_LIST,
      isNeedFeature: true,
      savedKey: CacheKeyEnum.carrierList,
      params,
    });
  }

  async getPartnerList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.PARTNER_LIST,
      isNeedFeature: true,
      capabilityKey: CapabilityEnum.partnerFilter,
      savedKey: CacheKeyEnum.partnerList,
      params,
    });
  }

  async getStoreList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.STORE_LIST,
      isNeedFeature: true,
      params,
    });
  }

  async getStaffList(
    params: BaseUserRequestModel,
  ): Promise<Array<BaseUserResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.STAFF_LIST,
      params,
      isNeedFeature: true,
    });
  }
}
